import { ArrowRightIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { CenteredLayout } from 'pages/employer_onboarding/CenteredLayout'
import { useAPIErrorBanner } from 'pages/employer_onboarding/useErrorBanner'
import { Button, Checkbox, CheckboxGroup, usePageTitle } from 'slab'
import { usePutQuotingProspectsId } from 'utils/web_api'
import { PutQuotingProspectsIdReasonsForChangingInsuranceItem } from 'utils/web_api.schemas'

import { emitEmployerOnboardingEvent } from '../../utils'
import { useProspect } from '../ProspectProvider'

export function NewBenefitsPage () {
  usePageTitle('Current benefits')

  const { prospect } = useProspect()
  const { setError, errorBanner } = useAPIErrorBanner({ fallbackMessage: 'Failed to save response' })
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()

  const updateProspectMutation = usePutQuotingProspectsId({
    mutation: {
      onSuccess: () => {
        navigateToNextStep()
      },
      onError: setError,
    },
  })

  const form = useForm({
    defaultValues: {
      reasons: {
        make_employees_happier: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.make_employees_happier),
        avoid_rate_increases: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.avoid_rate_increases),
        best_benefits: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.best_benefits),
        visibility_control_of_healthcare_costs: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs),
      },
    },
    mode: 'onTouched',
  })

  const handleSubmit = () => {
    if (!prospect?.id) return

    const data = form.getValues()

    const reasons = []
    if (data.reasons.make_employees_happier) reasons.push(PutQuotingProspectsIdReasonsForChangingInsuranceItem.make_employees_happier)
    if (data.reasons.avoid_rate_increases) reasons.push(PutQuotingProspectsIdReasonsForChangingInsuranceItem.avoid_rate_increases)
    if (data.reasons.best_benefits) reasons.push(PutQuotingProspectsIdReasonsForChangingInsuranceItem.best_benefits)
    if (data.reasons.visibility_control_of_healthcare_costs) reasons.push(PutQuotingProspectsIdReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs)

    emitEmployerOnboardingEvent('[ER] New to benefits info added', {
      reason: reasons
    })

    updateProspectMutation.mutate({
      id: prospect?.id,
      data: {
        reasons_for_changing_insurance: reasons as PutQuotingProspectsIdReasonsForChangingInsuranceItem[],
      },
    })
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading='Congrats on taking the first step towards giving your team benefits they’ll love!'
        subheading='Every team is different and we want to tailor your benefits strategy to match your unique needs.'
      />
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <CenteredLayout.Container>
          <CheckboxGroup label='What’s making you want to change?'>
            <Checkbox
              {...form.register('reasons.make_employees_happier')}
              label='Making sure my employees are happy'
            />
            <Checkbox
              {...form.register('reasons.avoid_rate_increases')}
              label='Avoiding unexpected rate increases'
            />
            <Checkbox
              {...form.register('reasons.best_benefits')}
              label='Providing the best benefits that I can afford'
            />
            <Checkbox
              {...form.register('reasons.visibility_control_of_healthcare_costs')}
              label='Visibility and control over healthcare costs'
            />
          </CheckboxGroup>
          {errorBanner}
        </CenteredLayout.Container>
        <CenteredLayout.Buttons>
          <Button variant='secondary' onClick={navigateToPreviousStep}>
            Back
          </Button>
          <Button
            variant='primary'
            type='submit'
            rightIcon={ArrowRightIcon}
            disabled={updateProspectMutation.isPending}
          >
            Next
          </Button>
        </CenteredLayout.Buttons>
      </form>
    </CenteredLayout>
  )
}
